import { Fragment } from "react";
import { ArrowLeftIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

export type BreadcrumbSegment = {
  labelKey: string;
  href?: string;
};

type SettingsPageHeaderProps = {
  /** Breadcrumb segments. Last segment is the current page (no href needed). */
  breadcrumbs?: BreadcrumbSegment[];
  /** @deprecated Use breadcrumbs instead. Locale key for a single breadcrumb segment. */
  navKey?: string;
  /** Locale key for the page title */
  titleKey: string;
  /** Optional locale key for a subtitle below the title */
  subtitleKey?: string;
  /** Optional back link href (shows ← Back button above title) */
  backHref?: string;
  /** Optional ReactNode rendered inline after the title (e.g. scope badge) */
  titleTrailing?: React.ReactNode;
  /** Optional ReactNode rendered on the right side (e.g. action buttons) */
  trailing?: React.ReactNode;
};

export function SettingsPageHeader({
  breadcrumbs,
  navKey,
  titleKey,
  subtitleKey,
  backHref,
  titleTrailing,
  trailing,
}: SettingsPageHeaderProps) {
  const { t } = useTranslation();

  // Build breadcrumb segments from either new prop or legacy navKey
  const segments: BreadcrumbSegment[] = breadcrumbs ?? [
    { labelKey: "settings.breadcrumb.root", href: "/settings" },
    { labelKey: navKey ?? titleKey },
  ];

  return (
    <div className="flex flex-wrap items-start justify-between gap-4 border-border/60 border-b pb-4">
      <div>
        {backHref && (
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="mb-2 -ml-3 w-fit text-muted-foreground"
          >
            <Link to={backHref}>
              <ArrowLeftIcon className="size-4" />
              {t("webview.back")}
            </Link>
          </Button>
        )}
        <nav className="flex items-center gap-1 text-[12px] text-muted-foreground">
          {segments.map((seg, i) => (
            <Fragment key={`${seg.labelKey}-${i}`}>
              {i > 0 && <span className="mx-0.5">/</span>}
              {seg.href ? (
                <Link
                  to={seg.href}
                  className="hover:text-foreground transition-colors"
                >
                  {t(seg.labelKey)}
                </Link>
              ) : (
                <span className="text-foreground">{t(seg.labelKey)}</span>
              )}
            </Fragment>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <h1 className="font-semibold text-[22px] tracking-[-0.01em]">
            {t(titleKey)}
          </h1>
          {titleTrailing}
        </div>
        {subtitleKey && (
          <p className="mt-1 max-w-[760px] text-[13px] text-muted-foreground leading-relaxed">
            {t(subtitleKey)}
          </p>
        )}
      </div>
      {trailing}
    </div>
  );
}
